import { useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'

/**
 * A schedule id can point at the legacy `gameSchedules` table or one of the two tables it was
 * split into (`schedulesRecurring`, `schedulesOneOff`). Keep in sync with `scheduleIdValidator`
 * in the convex package.
 */
export type ScheduleId = Id<'gameSchedules'> | Id<'schedulesRecurring'> | Id<'schedulesOneOff'>

export interface TableConfigParam {
  name: string
  difficulty: string
  numberOfTables: number
  blindTimer: number
  startingChips: number
  turnDuration: number
  bigBlind?: number
  smallBlind?: number
  maxPlayers?: number
  minPlayers?: number
}

export interface CreateScheduledTournamentParams {
  name: string
  hostId: string
  hostName: string
  scheduledStartTime: number
  tableConfigs: TableConfigParam[]
}

export interface UpdateScheduleParams {
  scheduleId: ScheduleId
  name?: string
  scheduledStartTime?: number
  blindTimer?: number
  startingChips?: number
  turnDuration?: number
  usersToNotify?: string[]
  /** Days of week (0-6) the schedule repeats on. */
  repeatDaysOfWeekUTC?: number[]
  tableConfigs?: TableConfigParam[]
}

export interface CreatePublicDailyScheduleParams {
  name: string
  /** Unix timestamp whose UTC time-of-day defines the slot time. */
  scheduledStartTime: number
  /** Days of week (0-6) the schedule repeats on; at least one. */
  repeatDaysOfWeekUTC: number[]
  tableConfigs: TableConfigParam[]
  usersToNotify?: string[]
}

export function useConvexScheduledTournament() {
  const createPrivateScheduleMutation = useMutation(api.gameSchedules.mutations.createPrivateSchedule)
  const createPublicDailyScheduleMutation = useMutation(api.gameSchedules.mutations.createPublicDailySchedule)
  const updateScheduleMutation = useMutation(api.gameSchedules.mutations.updateSchedule)
  const cancelScheduleMutation = useMutation(api.gameSchedules.mutations.cancelSchedule)

  const createScheduledTournament = async (params: CreateScheduledTournamentParams) => {
    return await createPrivateScheduleMutation(params)
  }

  const createPublicDailySchedule = async (params: CreatePublicDailyScheduleParams) => {
    return await createPublicDailyScheduleMutation(params)
  }

  const updateSchedule = async (params: UpdateScheduleParams) => {
    return await updateScheduleMutation(params)
  }

  const cancelSchedule = async (scheduleId: ScheduleId) => {
    return await cancelScheduleMutation({ scheduleId })
  }

  return {
    createScheduledTournament,
    createPublicDailySchedule,
    updateSchedule,
    cancelSchedule,
    createPrivateScheduleMutation,
    createPublicDailyScheduleMutation,
    updateScheduleMutation,
    cancelScheduleMutation
  }
}
